import { useCallback, useLayoutEffect, useState } from "react";

// Dashed "matrix" connectors between arbitrary org cards (Miro style).
// Rendered INSIDE the zoom wrapper so CSS zoom scales it for free — all
// math is therefore done in unzoomed local coordinates.
export function LinkLayer({ links, wrapperRef, zoom, recalcKey, canEdit, onRequestDelete }) {
  const [paths, setPaths] = useState([]);

  const compute = useCallback(() => {
    const wrap = wrapperRef.current;
    if (!wrap) {
      setPaths([]);
      return;
    }
    const wr = wrap.getBoundingClientRect();
    const out = [];
    (links || []).forEach((l) => {
      const a = wrap.querySelector(`[data-testid="org-chart-node-${l.from_id}"]`);
      const b = wrap.querySelector(`[data-testid="org-chart-node-${l.to_id}"]`);
      if (!a || !b) return; // endpoint collapsed/hidden → skip drawing
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      const A = {
        x: (ra.left - wr.left) / zoom,
        y: (ra.top - wr.top) / zoom,
        w: ra.width / zoom,
        h: ra.height / zoom,
      };
      const B = {
        x: (rb.left - wr.left) / zoom,
        y: (rb.top - wr.top) / zoom,
        w: rb.width / zoom,
        h: rb.height / zoom,
      };
      const acx = A.x + A.w / 2;
      const acy = A.y + A.h / 2;
      const bcx = B.x + B.w / 2;
      const bcy = B.y + B.h / 2;
      let p1;
      let p2;
      let c1;
      let c2;
      if (Math.abs(bcx - acx) > Math.abs(bcy - acy)) {
        const toRight = bcx > acx;
        p1 = { x: toRight ? A.x + A.w : A.x, y: acy };
        p2 = { x: toRight ? B.x : B.x + B.w, y: bcy };
        const dx = Math.max(40, Math.abs(p2.x - p1.x) / 2);
        c1 = { x: p1.x + (toRight ? dx : -dx), y: p1.y };
        c2 = { x: p2.x + (toRight ? -dx : dx), y: p2.y };
      } else {
        const toBottom = bcy > acy;
        p1 = { x: acx, y: toBottom ? A.y + A.h : A.y };
        p2 = { x: bcx, y: toBottom ? B.y : B.y + B.h };
        const dy = Math.max(40, Math.abs(p2.y - p1.y) / 2);
        c1 = { x: p1.x, y: p1.y + (toBottom ? dy : -dy) };
        c2 = { x: p2.x, y: p2.y + (toBottom ? -dy : dy) };
      }
      out.push({
        id: l.id,
        label: l.label,
        d: `M ${p1.x} ${p1.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${p2.x} ${p2.y}`,
        mid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
      });
    });
    setPaths(out);
  }, [links, wrapperRef, zoom]);

  useLayoutEffect(() => {
    compute();
    const t = setTimeout(compute, 300);
    window.addEventListener("resize", compute);
    let ro;
    if (wrapperRef.current && window.ResizeObserver) {
      ro = new ResizeObserver(compute);
      ro.observe(wrapperRef.current);
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", compute);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compute, recalcKey]);

  if (!paths.length) return null;
  return (
    <svg
      className="absolute inset-0 h-full w-full overflow-visible"
      style={{ pointerEvents: "none", zIndex: 5 }}
      data-testid="org-link-layer"
    >
      <defs>
        <marker id="org-link-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="#64748b" />
        </marker>
      </defs>
      {paths.map((p) => (
        <g key={p.id}>
          <path
            d={p.d}
            fill="none"
            stroke="#64748b"
            strokeWidth="2"
            strokeDasharray="6 4"
            markerEnd="url(#org-link-arrow)"
            style={{
              pointerEvents: canEdit ? "visibleStroke" : "none",
              cursor: canEdit ? "pointer" : "default",
            }}
            onClick={() => canEdit && onRequestDelete && onRequestDelete(p.id)}
            data-testid={`org-link-${p.id}`}
          >
            {canEdit && <title>Bağlantıyı silmek için tıkla</title>}
          </path>
          {p.label && (
            <text
              x={p.mid.x}
              y={p.mid.y - 6}
              textAnchor="middle"
              className="fill-slate-500 text-[10px] font-semibold"
            >
              {p.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
